/**
 * Widgets de la pestaña «Análisis» de la ficha de un contacto. Todos leen la misma
 * respuesta de GET /api/contact-insights/:id y la agrupan distinto (`view`).
 *
 * Mismo formato que las entradas del dashboard (widgetCatalog.js): `type` se guarda en
 * el layout, `category`/`icon`/`preview` son para el catálogo, `size`/`min` para la
 * grilla. `chart` es el tipo de gráfico inicial (se puede cambiar); `finance: true` solo aparece con
 * permiso de finanzas (el back tampoco manda montos sin él).
 */
import { placeNewWidget } from "@/components/gotrading/dashboard/widgetCatalog";

export const CONTACT_WIDGETS = [
  {
    type: "contact:summary",
    view: "summary",
    category: "general",
    icon: "id-badge",
    preview: "kpi",
    size: { w: 12, h: 4 },
    min: { w: 4, h: 3 },
  },
  {
    type: "contact:operations",
    view: "operations",
    category: "general",
    icon: "list",
    preview: "list",
    size: { w: 6, h: 10 },
    min: { w: 3, h: 5 },
  },
  {
    type: "contact:by-month",
    view: "byMonth",
    category: "general",
    icon: "calendar-alt",
    preview: "columns",
    chart: "column",
    size: { w: 6, h: 9 },
    min: { w: 3, h: 5 },
  },
  {
    type: "contact:by-role",
    view: "byRole",
    category: "general",
    icon: "user-tag",
    preview: "bars",
    chart: "donut",
    size: { w: 4, h: 9 },
    min: { w: 3, h: 5 },
  },
  {
    type: "contact:products",
    view: "products",
    category: "general",
    icon: "box",
    preview: "bars",
    chart: "bar",
    size: { w: 6, h: 10 },
    min: { w: 3, h: 5 },
  },
  {
    type: "contact:counterparts",
    view: "counterparts",
    category: "general",
    icon: "handshake",
    preview: "bars",
    chart: "bar",
    size: { w: 6, h: 10 },
    min: { w: 3, h: 5 },
  },
  {
    type: "contact:volume",
    view: "volume",
    category: "finance",
    icon: "chart-line",
    preview: "columns",
    chart: "column",
    finance: true,
    size: { w: 6, h: 9 },
    min: { w: 3, h: 5 },
  },
  {
    type: "contact:products-amount",
    view: "productsAmount",
    category: "finance",
    icon: "dollar-sign",
    preview: "bars",
    chart: "bar",
    finance: true,
    size: { w: 6, h: 10 },
    min: { w: 3, h: 5 },
  },
  {
    type: "contact:money-flow",
    view: "moneyFlow",
    category: "finance",
    icon: "exchange-alt",
    preview: "columns",
    chart: "column",
    finance: true,
    needsFinanceModule: true,
    size: { w: 6, h: 9 },
    min: { w: 3, h: 5 },
  },
];

/** `ctx.finance`: el back dijo que puede ver montos; `ctx.financeModule`: hay cobros y pagos. */
export function buildContactCatalog(ctx) {
  return CONTACT_WIDGETS.filter(
    (e) =>
      (!e.finance || ctx.finance) &&
      (!e.needsFinanceModule || ctx.financeModule)
  ).map((e) => ({
    ...e,
    kind: "contact",
    config: e.chart ? { chart: e.chart } : {},
  }));
}

/** El de fábrica: resumen arriba, operaciones por mes y la lista, productos y, con finanzas, el volumen. */
export function buildDefaultContactLayout(catalog) {
  const byType = Object.fromEntries(catalog.map((e) => [e.type, e]));
  const wanted = [
    "contact:summary",
    "contact:by-month",
    "contact:operations",
    "contact:products",
    "contact:volume",
    "contact:counterparts",
  ];
  const out = [];
  wanted.forEach((type) => {
    const e = byType[type];
    if (!e) return;
    const pos = placeNewWidget(out, e.size.w);
    out.push({
      i: type,
      type,
      x: pos.x,
      y: pos.y,
      w: e.size.w,
      h: e.size.h,
      config: { ...e.config },
    });
  });
  return out;
}
